import React, { FC, useContext, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useKeepSendingRequest } from "go-core/hooks/useKeepSendingRequest";
import { UseKeepSendingRequestState } from "go-core/hooks/utils/types";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	DeliveryEstimationApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";
import { OrdersContext } from "../../../../../../services/context";
import { isWoltDriveAssigned } from "../../../../../../services/orderDelivery";
import { liveOrderParamsInclude } from "../../../../../../services/orderSynchronizer";
import { LiveOrderDeliveryOptionType } from "../../../../../../services/types";
import {
	getDefaultDeliveryOption,
	getDefaultOrderEstimatedDeliveryData,
	getNumberOfMinutesFromNowToTheDate,
	isWoltDriveEnabled,
} from "../../../../../../services/utils";
import LiveOrderModalEstimatedTimeInfo from "../LiveOrderModalEstimatedTimeInfo";
import LiveOrderAssignDeliveryEmployeeModalDeliveryOptions from "./LiveOrderAssignDeliveryEmployeeModalDeliveryOptions";
import LiveOrderAssignDeliveryEmployeeModalTimeOptions from "./LiveOrderAssignDeliveryEmployeeModalTimeOptions";
import LiveOrdersAssignDeliveryEmployeeModalOptions from "./LiveOrdersAssignDeliveryEmployeeModalOptions";

interface Props {
	isShown: boolean;
	onHide: () => void;
	order: LiveOrderApi;
}

const LiveOrdersAssignDeliveryEmployeeModal: FC<Props> = ({ isShown, onHide, order }) => {
	const { t } = useTranslation();
	const size = useWindowSize();
	const organization = useSelector(selectOrganization);
	const defaultDeliveryOption = getDefaultDeliveryOption(organization, order);
	const ordersContext = useContext(OrdersContext);
	const { addFlash, addSuccessFlash } = useFlash();
	const isWoltDrive = isWoltDriveAssigned(order);
	const [estimatedDeliveryData, setEstimatedDeliveryData] = useState<DeliveryEstimationApi | undefined>();
	const [firstEstimatedDeliveryData, setFirstEstimatedDeliveryData] = useState<DeliveryEstimationApi | undefined>(
		isWoltDrive ? getDefaultOrderEstimatedDeliveryData(order) : undefined
	);
	const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<LiveOrderDeliveryOptionType | undefined>(
		defaultDeliveryOption
	);
	const orderDate = order?.estimated_delivery_at ?? order?.requested_pickup_at;
	const orderEstimatedPreparationInMinutes = useMemo(() => {
		return getNumberOfMinutesFromNowToTheDate(order.estimated_delivery_at);
	}, [order?.estimated_delivery_at]);
	const [selectedTimeOption, setSelectedTimeOption] = useState<number | string | undefined>(
		isWoltDrive ? orderDate : undefined
	);
	const assignedDeliveryEmployee =
		order.delivery?.delivery_employee?.reference_id || order.delivery?.delivery_employee?.id || isWoltDrive;
	const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(
		order?.delivery?.delivery_employee?.reference_id
	);
	const [loading, setLoading] = useState(false);
	const [unassignLoading, setUnassignLoading] = useState(false);
	const currency = organization.currency || "PLN";
	const confirmation = useConfirmation();
	const form = useForm<{ scheduled_delivery_at?: string }>({
		criteriaMode: "all",
		defaultValues: {
			scheduled_delivery_at: typeof selectedTimeOption === "string" ? selectedTimeOption : undefined,
		},
	});
	const { setValue } = form;
	const [estimatingLoading, setEstimatingLoading] = useState(false);

	const getParsedEmployeeDataForm = (unassign?: boolean) => {
		let employeeId;
		if (selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE) {
			employeeId = unassign
				? undefined
				: estimatedDeliveryData?.id || order?.delivery?.delivery_employee?.reference_id;
		}
		if (selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER) {
			employeeId = unassign ? undefined : selectedEmployeeId;
		}

		const deliveryPickupInMinutes =
			typeof selectedTimeOption === "string" ? 0 : estimatedDeliveryData?.delivery_pickup_in_minutes;

		return {
			delivery_employee_id: employeeId,
			delivery_cost: { amount: estimatedDeliveryData?.price?.amount || 0, currency },
			delivery_pickup_in_minutes: deliveryPickupInMinutes,
			scheduled_delivery_at: typeof selectedTimeOption === "string" ? selectedTimeOption : undefined,
		};
	};
	const showWoltExtraChargeAlert = (isSendingRequests?: boolean, unassign?: boolean) => {
		if (unassign || isSendingRequests) return false;

		if (isWoltDrive) {
			if (selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER) return false;
			return true;
		}

		return false;
	};

	const handleChangeEmployee = async (unassign?: boolean, sendingRequestState?: UseKeepSendingRequestState) => {
		const { allRequestsSent, isSendingRequests, forceStopSendingRequests } = sendingRequestState ?? {};
		let firstRequestStartTimestamp = 0;
		try {
			if (unassign) {
				setUnassignLoading(true);
			} else {
				setLoading(true);
			}
			if (showWoltExtraChargeAlert(isSendingRequests, unassign)) {
				await confirmation({
					title: t("lib:common.word.confirmation"),
					message: t("modules.live_order.field.wolt_extra_charge.title"),
				});
			}
			const data = getParsedEmployeeDataForm(unassign);
			const params: Record<string, any> = { include: liveOrderParamsInclude };
			firstRequestStartTimestamp = performance.now();

			const res = await api.organization().assignEmployeeDeliveryToOrder(order.id, data, params);
			let orderToUpdate = ordersContext.orders.find((o) => o.id === order.id);
			if (orderToUpdate) {
				orderToUpdate = {
					...res,
					preparation_status: {
						...orderToUpdate.preparation_status,
					},
				};
				ordersContext.updateOrder(orderToUpdate as LiveOrderApi);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
			onHide();
		} catch (err: any) {
			const wrongTerminalError = err.errors?.find(
				(error: Record<string, any>) => error.code === "wrong_order_terminal"
			);
			if (wrongTerminalError) {
				if (!isSendingRequests) {
					if (unassign) {
						keepSendingUnassignEmployee(firstRequestStartTimestamp);
					} else {
						keepSendingRequests(firstRequestStartTimestamp);
					}
				}

				if (allRequestsSent) {
					const flashMessage = t("constraints.wrong_terminal_order", {
						terminal_name: wrongTerminalError.terminal_name,
					});
					addFlash({
						type: "danger",
						msg: flashMessage,
						messageAlreadyCreated: true,
					});
				} else {
					return;
				}
			} else {
				handleError.alert(err, addFlash);
			}
		}
		if (unassign) {
			setUnassignLoading(false);
		} else {
			setLoading(false);
		}

		if (forceStopSendingRequests !== undefined) {
			forceStopSendingRequests.current = true;
		}
	};

	const { keepSendingRequests } = useKeepSendingRequest((sendingRequestState: UseKeepSendingRequestState) =>
		handleChangeEmployee(false, sendingRequestState)
	);
	const { keepSendingRequests: keepSendingUnassignEmployee } = useKeepSendingRequest(
		(sendingRequestState: UseKeepSendingRequestState) => handleChangeEmployee(true, sendingRequestState)
	);

	const isSubmitButtonDisabled = () => {
		if (!selectedDeliveryOption) return true;
		if (selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE) {
			return !selectedTimeOption;
		}
		if (selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER) {
			return !selectedEmployeeId || selectedEmployeeId.includes("WOLT_DRIVE");
		}
	};

	const handleUnselectDeliveryOption = (option: LiveOrderDeliveryOptionType | undefined) => {
		if (option === LiveOrderDeliveryOptionType.OWN_DRIVER) {
			setSelectedTimeOption(undefined);
			setValue("scheduled_delivery_at", undefined);
			const orderDeliveryEmployeeId =
				order?.delivery?.delivery_employee?.reference_id || order?.delivery?.delivery_employee?.id;
			if (orderDeliveryEmployeeId) {
				setSelectedEmployeeId(orderDeliveryEmployeeId.toString());
			} else setSelectedEmployeeId(undefined);
			setEstimatedDeliveryData(undefined);
		} else if (option === LiveOrderDeliveryOptionType.WOLT_DRIVE) {
			setSelectedTimeOption(orderDate);
			setValue("scheduled_delivery_at", orderDate);
		} else {
			setSelectedTimeOption(undefined);
			setSelectedEmployeeId(undefined);
		}
	};

	return (
		<Modal
			onHide={onHide}
			show={isShown}
			className={`assign-delivery-employee-modal ${
				selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER ? "limited-height" : ""
			}`}
			size={size.isMobile ? "sm" : "lg"}
		>
			<Modal.Header closeButton>
				<div className="d-flex justify-content-between align-items-center w-100">
					<Modal.Title>
						{`${t("modules.live_order.header.assign_driver_to_order.title")} #${order.number}`}
					</Modal.Title>
					{assignedDeliveryEmployee && (
						<ButtonLoading
							loading={unassignLoading}
							variant="danger"
							className="me-2"
							onClick={() => handleChangeEmployee(true)}
						>
							{t("modules.live_order.action.unassign_driver.title")}
						</ButtonLoading>
					)}
				</div>
			</Modal.Header>
			<Modal.Body>
				{selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE &&
					(order.requested_pickup_at || order.pickup_at) && (
						<>
							<h5 className="mb-2">{t("modules.live_order.field.client_pick_up_date.title")}</h5>
							<div className="live-order-date-box">
								{FormatDate((order.requested_pickup_at ?? order.pickup_at) as string)}
							</div>
							<hr />
						</>
					)}
				{isWoltDriveEnabled(organization) && (
					<>
						<h5 className="mb-2">{t("modules.live_order.field.choose_delivery.title")}</h5>
						<LiveOrderAssignDeliveryEmployeeModalDeliveryOptions
							order={order}
							orderEstimatedPreparationInMinutes={orderEstimatedPreparationInMinutes}
							orderDate={orderDate}
							selectedDeliveryOption={selectedDeliveryOption}
							setSelectedDeliveryOption={setSelectedDeliveryOption}
							selectedTimeOption={selectedTimeOption}
							firstEstimatedDeliveryData={firstEstimatedDeliveryData}
							setFirstEstimatedDeliveryData={setFirstEstimatedDeliveryData}
							setEstimatedDeliveryData={setEstimatedDeliveryData}
							estimatedDeliveryData={estimatedDeliveryData}
							handleToggle={handleUnselectDeliveryOption}
							estimatingLoading={estimatingLoading}
							setEstimatingLoading={setEstimatingLoading}
						/>
					</>
				)}
				{selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER && (
					<div className={`d-flex align-items-center w-100`}>
						<h5 className="mb-2">{t("modules.live_order.field.choose_delivery_employee.title")}</h5>
					</div>
				)}

				{selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER && (
					<LiveOrdersAssignDeliveryEmployeeModalOptions
						setSelectedEmployeeId={setSelectedEmployeeId}
						selectedEmployeeId={selectedEmployeeId}
					/>
				)}

				{selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE && (
					<LiveOrderAssignDeliveryEmployeeModalTimeOptions
						order={order}
						form={form}
						setEstimatedDeliveryData={setEstimatedDeliveryData}
						orderDate={orderDate}
						selectedTimeOption={selectedTimeOption}
						selectedDeliveryOption={selectedDeliveryOption}
						setSelectedTimeOption={setSelectedTimeOption}
						estimatingLoading={estimatingLoading}
						setEstimatingLoading={setEstimatingLoading}
					/>
				)}
				{selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE && estimatedDeliveryData && (
					<LiveOrderModalEstimatedTimeInfo deliveryEstimation={estimatedDeliveryData} />
				)}
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading
					loading={loading}
					onClick={() => handleChangeEmployee()}
					type="submit"
					disabled={isSubmitButtonDisabled()}
				>
					{t("lib:common.action.save")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default LiveOrdersAssignDeliveryEmployeeModal;
